// frontend/src/pages/ForgotPassword.js
import React, { useState } from "react";
import { supabase } from "../supabase";
import { Link } from "react-router-dom";
import TurnstileWidget from "../components/TurnstileWidget";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!captchaToken) {
      setError("セキュリティチェックを完了してください");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
        captchaToken,
      });

      if (error) throw error;

      setSent(true);
    } catch (error) {
      console.error("パスワードリセットエラー:", error);
      // captcha 関連は分かりやすく出す
      const msg = String(error?.message || '');
      if (msg.toLowerCase().includes('captcha')) {
        setError("セキュリティチェックに失敗しました。もう一度お試しください。");
      } else {
        setError(`メールの送信に失敗しました: ${msg || 'メールアドレスをご確認ください'}`);
      }
      setCaptchaToken(""); // 再チャレンジ用にトークンクリア
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              メールを送信しました
            </h2>
            <p className="text-gray-600 mb-6">
              {email} 宛にパスワードリセット用のリンクを送信しました。
              メールをご確認ください。
            </p>
            <p className="text-sm text-gray-500 mb-6">
              ※ メールが届かない場合は、迷惑メールフォルダもご確認ください。
            </p>
            <Link
              to="/login"
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 px-6 rounded-lg transition"
            >
              ログイン画面に戻る
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            パスワードをお忘れですか？
          </h1>
          <p className="text-gray-600">
            登録済みのメールアドレスを入力してください。
            パスワードリセット用のリンクをお送りします。
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              メールアドレス
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="your@email.com"
            />
          </div>

          <div className="flex justify-center" style={{ minHeight: "65px" }}>
            <TurnstileWidget
              siteKey={TURNSTILE_SITE_KEY}
              onVerify={(t) => setCaptchaToken(t)}
              onExpire={() => setCaptchaToken("")}
              onError={() => setCaptchaToken("")}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !captchaToken}
            className={`w-full py-3 px-4 rounded-lg font-medium text-white transition ${
              loading || !captchaToken
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700"
            }`}
          >
            {loading ? "送信中..." : "リセットリンクを送信"}
          </button>

          <div className="text-center">
            <Link
              to="/login"
              className="text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              ← ログイン画面に戻る
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
